import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { ArrowRight, Check, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatErrorDetail } from '../utils/formatError';
import './HomePage.css';

const Pricing = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleCheckout = async (packageId) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    setCheckoutLoading(packageId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, {
        package_id: packageId,
        origin_url: origin,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const tiers = [
    {
      name: 'Free Assessment',
      price: 'Free',
      priceNote: 'No credit card required',
      description: 'Find out where you stand. The assessment places you on the right pathway for you.',
      includes: [
        'Stage 1 Assessment (45–90 min)',
        'Your personalised readiness score',
        'Your recommended pathway',
        'A clear next step',
      ],
      cta: 'Start Free',
      ctaAction: () => user ? navigate('/stage1-assessment') : navigate('/auth'),
      highlight: false,
      packageId: null,
    },
    {
      name: 'Pathway A: Founders Track',
      price: '$TBA',
      priceNote: 'Stages 2–9, one flat fee',
      description: 'The full guided build, from idea generation through to scaling your business.',
      includes: [
        'All eight remaining stages',
        'Brian, your AI guide, always on',
        'Templates and tools for every stage',
        'Your Springboard from Stage 4',
        'Real human mentor access',
      ],
      cta: user?.stages_unlocked ? 'Already Unlocked' : 'Unlock After Assessment',
      ctaAction: () => user?.pathway === 'A' && !user?.stages_unlocked ? handleCheckout('pathway_a') : navigate('/stage1-assessment'),
      highlight: true,
      packageId: 'pathway_a',
      disabled: user?.stages_unlocked,
    },
    {
      name: 'Pathway B: Employment Track',
      price: '$TBA',
      priceNote: 'Stage 2, one flat fee',
      description: 'Your strengths point to the right employed role. We help you find it.',
      includes: [
        'Stage 2: Employment direction',
        'CV rebuild and strengths finder',
        'Job search support and tracking',
        'Human mentor access',
      ],
      cta: 'Unlock After Assessment',
      ctaAction: () => user?.pathway === 'B' && !user?.stages_unlocked ? handleCheckout('pathway_b') : navigate('/stage1-assessment'),
      highlight: false,
      packageId: 'pathway_b',
    },
    {
      name: 'Pathway C: Mindset Discovery',
      price: '$TBA',
      priceNote: 'Stages 2–4, one flat fee',
      description: 'Build the foundations first. Confidence, clarity and a direction that fits.',
      includes: [
        'Stages 2–4: Foundation building',
        'Self-assessment and service definition',
        'Mindset and resilience tools',
        'Human mentor access',
      ],
      cta: 'Unlock After Assessment',
      ctaAction: () => user?.pathway === 'C' && !user?.stages_unlocked ? handleCheckout('pathway_c') : navigate('/stage1-assessment'),
      highlight: false,
      packageId: 'pathway_c',
    },
  ];

  return (
    <div className="home-page">
      {/* Nav */}
      <nav className="home-nav scrolled">
        <div className="nav-inner">
          <a href="/" className="nav-logo" onClick={(e) => { e.preventDefault(); navigate('/'); }}>spring<span style={{ display: 'inline-block', width: 6, height: 6, background: '#F6ADBC', borderRadius: '50%', marginLeft: 1, verticalAlign: 'baseline', position: 'relative', top: -2 }} /></a>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              className="btn-secondary"
              style={{ padding: '10px 20px', fontSize: '14px' }}
              onClick={() => navigate('/')}
            >
              <ArrowLeft style={{ width: 16, height: 16, marginRight: 4 }} />
              Back
            </button>
            <button className="nav-cta" onClick={() => user ? navigate('/portal') : navigate('/auth')} data-testid="pricing-nav-cta">
              {user ? 'The Springboard' : 'Get Started'}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ paddingTop: '140px', paddingBottom: '40px', background: '#1E491A' }}>
        <div className="section-inner center">
          <h1 className="section-headline" style={{ fontSize: 'clamp(32px, 4vw, 48px)' }} data-testid="pricing-headline">
            Simple, honest pricing.
          </h1>
          <p className="section-body" style={{ color: '#CDCDCD', opacity: 0.85, maxWidth: '600px', margin: '0 auto' }}>
            Stage 1 is free. Everything after that is one flat fee, whatever your pace. Existing businesses can take Hub access on its own, no stages required.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section style={{ padding: '80px 24px', background: '#f5f5f5' }}>
        <div className="section-inner">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {tiers.map((tier, i) => (
              <div
                key={i}
                data-testid={`pricing-tier-${i}`}
                style={{
                  background: tier.highlight ? '#1a1a1a' : '#fff',
                  color: tier.highlight ? '#fff' : '#1a1a1a',
                  borderRadius: 0,
                  padding: '40px 32px',
                  display: 'flex',
                  flexDirection: 'column',
                  border: tier.highlight ? '2px solid #F6ADBC' : '2px solid transparent',
                  position: 'relative',
                }}
              >
                {tier.highlight && (
                  <span style={{
                    position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
                    background: '#F6ADBC', color: '#1E491A', padding: '4px 16px', borderRadius: 0,
                    fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px',
                  }}>
                    MOST POPULAR
                  </span>
                )}
                <h3 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 8px' }}>{tier.name}</h3>
                <div style={{ fontSize: '36px', fontWeight: 700, margin: '0 0 4px' }}>{tier.price}</div>
                <p style={{ fontSize: '13px', opacity: 0.7, margin: '0 0 20px' }}>{tier.priceNote}</p>
                <p style={{ fontSize: '15px', opacity: 0.85, margin: '0 0 24px', lineHeight: 1.5 }}>{tier.description}</p>
                <ul style={{ listStyle: 'none', margin: '0 0 32px', padding: 0, flex: 1 }}>
                  {tier.includes.map((item, j) => (
                    <li key={j} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', marginBottom: '10px' }}>
                      <Check style={{ width: 16, height: 16, color: tier.highlight ? '#F6ADBC' : '#16a34a', flexShrink: 0 }} />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={tier.ctaAction}
                  disabled={tier.disabled || checkoutLoading === tier.packageId}
                  data-testid={`pricing-cta-${i}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    background: tier.disabled ? '#94a3b8' : tier.highlight ? '#F6ADBC' : '#1a1a1a',
                    color: tier.disabled ? '#fff' : tier.highlight ? '#1a1a1a' : '#fff',
                    border: 'none', padding: '14px 28px', borderRadius: 0,
                    fontSize: '15px', fontWeight: 600,
                    cursor: tier.disabled ? 'default' : 'pointer',
                    opacity: tier.disabled ? 0.7 : 1,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => { if (!tier.disabled) { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)'; } }}
                  onMouseLeave={(e) => { e.target.style.transform = ''; e.target.style.boxShadow = ''; }}
                >
                  {checkoutLoading === tier.packageId ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {tier.cta}
                      {!tier.disabled && <ArrowRight style={{ width: 16, height: 16 }} />}
                    </>
                  )}
                </button>
                {/* Demo bypass */}
                {tier.packageId && !tier.disabled && user && (
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/payments/demo-bypass`, { package_id: tier.packageId }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success(`Demo: ${tier.name} unlocked`);
                        window.location.reload();
                      } catch (e) { toast.error('Failed'); }
                    }}
                    data-testid={`pricing-demo-bypass-${i}`}
                    style={{
                      background: 'none', border: 'none', color: tier.highlight ? '#fff' : '#999',
                      fontSize: '12px', cursor: 'pointer', marginTop: '10px', opacity: 0.6,
                      textDecoration: 'underline',
                    }}
                  >
                    Skip Payment (Demo)
                  </button>
                )}
              </div>
            ))}
          </div>

          <p style={{ textAlign: 'center', fontSize: '14px', color: '#666', marginTop: '40px' }}>
            Existing businesses can take Hub access on its own, no stages required. A human mentor is available as a monthly add-on.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-logo">spring<span style={{ display: 'inline-block', width: 5, height: 5, background: '#F6ADBC', borderRadius: '50%', marginLeft: 1, verticalAlign: 'baseline', position: 'relative', top: -1 }} /></span>
            <p>New Zealand's guided platform for career transitioners.</p>
          </div>
          <div className="footer-copy">
            <p>&copy; 2026 SPRING. Built in New Zealand, for New Zealanders.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
